import React from "react";
import { Box, Stack, CircularProgress, Button } from "@mui/material";
import Header from "components/Header";
import PageWrapper from "components/PageWrapper";
import { useNavigate, useParams } from "react-router";
import { InvestorAPI, KycAPI, MarkerAPI } from "../api";
import { fullName } from "../utils/investor";
import ProfileGroup from "./ProfileGroup";
import { useDispatch, useSelector } from "react-redux";
import { amlStoreActions, selectFromAmlStore } from "../amlSlice";
import { useQuery } from "@tanstack/react-query";
import { getInvestorAmlSubject } from "../api/aml";

const dataSelector = selectFromAmlStore((state) => ({
  kycReferences: state.kycReferences,
  entityTypes: state.entityTypes,
  markers: state.markers,
}));
const InvestorAmlProfile = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // react-query: fetch page data
  const {
    data: amlSubjectData,
    isLoading: amlSubjectLoading,
    error: amlSubjectError,
  } = useQuery({
    queryKey: ["investor-aml-subject", id],
    queryFn: () => getInvestorAmlSubject(id!),
    enabled: Boolean(id),
  });

  const { kycReferences, entityTypes, markers } = useSelector(dataSelector);
  const dispatch = useDispatch();
  const kycReferencesReady = kycReferences.length > 0;
  const entityTypesReady = entityTypes.length > 0;
  const markersReady = markers.length > 0;
  const loading =
    !kycReferencesReady ||
    !entityTypesReady ||
    !markersReady ||
    amlSubjectLoading;


  // load global data
  // Todo: move to app initialization
  React.useEffect(() => {
    if (!kycReferencesReady) {
      KycAPI.getKycReferenceList().then((response) => {
        dispatch(amlStoreActions.setKycReferences(response));
      });
    }
    if (!entityTypesReady) {
      InvestorAPI.getEntityTypes().then((response) => {
        dispatch(amlStoreActions.setEntityTypes(response));
      });
    }
    if (!markersReady) {
      MarkerAPI.getMarkers().then((response) => {
        dispatch(amlStoreActions.setMarkers(response));
      });
    }
  }, []);

  const subTitle = amlSubjectData
    ? `Review Investor Profile for ${amlSubjectData.name} (${
      amlSubjectData.identifier
    })`
    : "Review Investor Profile";

  return (
    <PageWrapper>
      <Stack justifyContent="space-between" direction="row">
        <Header title="AML Profile" subtitle={subTitle} />
        <Button
          variant="text"
          sx={{
            height: "32px",
            backgroundColor: "transparent",
            color: "primary.main",
            padding: "6px 8px",
            minWidth: "64px",
            fontSize: "0.875rem",
            fontWeight: 500,
            lineHeight: 1.75,
            borderRadius: "4px",
            textTransform: "uppercase",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.04)",
              boxShadow: "none",
            },
          }}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Stack>
      <Stack direction="column" rowGap="30px" sx={{ width: "100%" }}>
        {loading && <LoadingContent />}
        {!loading && (amlSubjectError) && (
          <Box
            sx={{
              minHeight: 400,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "error.main",
            }}
          >
            Investor detail not found
          </Box>
        )}
        {!loading && <ProfileGroup investorId={id!} />}
      </Stack>
    </PageWrapper>
  );
};

const LoadingContent = () => {
  return (
    <Box
      sx={{
        minHeight: 400,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <CircularProgress />
    </Box>
  );
};
export default InvestorAmlProfile;
